import axios from "axios";
import Config from "../config";
import { sendPageViewBeacon as _sendPageView, sendConsentBeacon as _sendConsent } from "../utils/analyticsBeacon";
import {
  getGuestJwt,
  setGuestJwt,
  clearGuestJwt,
  reportGuestSession,
} from "../utils/guestAuth";
import { isEmbedMode } from "../modules/selfService/embed/embedParams";
import { isAuthFailure } from "../utils/ceToken";

const createAction = (type, key) => (payload) => ({ type, payload, key });

const fetchData = async (
  url,
  method,
  data,
  dispatch,
  successActionType,
  skipToken,
  useBearer,
  getState
) => {
  try {
    const token = getState ? (getState()?.IDToken || localStorage.getItem("token")) : localStorage.getItem("token");
    const guestJwt = getGuestJwt();
    const headers = token
      ? { Authorization: useBearer ? `Bearer ${token}` : token }
      : guestJwt
        ? { Authorization: `Bearer ${guestJwt}` }
        : {};
    if (token || guestJwt || skipToken) {
      const response = await axios({
        method,
        url: Config.dev.url[url],
        headers,
        data,
      });

      dispatch(successActionType(response.data));
      return response?.data;
    }
  } catch (error) {
    console.error("Error: ", error.message);
    // The axios interceptor (utils/ceToken) has already refreshed and retried
    // an auth failure on a cePublic token; refreshing again here would rotate
    // the token twice for one failure.
    if (!error?.ceTokenRefreshHandled && shouldRefreshCeToken(url, error)) {
      // Fetch the replacement first and let getCeToken overwrite the stored
      // token: getImage reads localStorage.token on every media fetch, so
      // clearing it up front failed every image in flight until the new token
      // landed. Nothing else in storage is touched — the old
      // localStorage.clear() also dropped queued map interactions and the
      // stored site name.
      await dispatch(getCeToken());
    }
  }
};

const shouldRefreshCeToken = (url, error) => {
  if (!isAuthFailure(error)) return false;
  // getCeToken failing must not call itself again.
  if (url === "getCeToken") return false;
  if (!localStorage.getItem("token")) return false;
  // An embed mirrors its origin-scoped token into localStorage (EmbedApp);
  // getCeToken would swap it for a generic public token mid-session.
  if (isEmbedMode()) return false;
  return true;
};

export const setCEToken = createAction("SET_CE_TOKEN");
export const setAssets = createAction("SET_ASSETS");
export const setAssetComponents = createAction("SET_ASSET_COMPONENTS");
export const setCeQuestion = createAction("SET_CE_QUESTIONS", "ceQuestions");
export const setCreateVisitor = createAction(
  "SET_CREATE_VISITOR",
  "createVisitor"
);
export const setChatCreateVisitor = createAction(
  "SET_CHAT_CREATE_VISITOR",
  "chatCreateVisitor"
);
export const setEditQAFeedback = createAction(
  "SET_EDIT_QA_FEEDBACK",
  "editQAFeedback"
);
export const setAddQAFeedback = createAction(
  "SET_ADD_QA_FEEDBACK",
  "addQAFeedback"
);
export const setAiRagChat = createAction(
  "SET_AI_RAG_CHAT",
  "aiRagChat"
);
export const setChat2CreateVisitor = createAction(
  "SET_CHAT2_CREATE_VISITOR",
  "chat2CreateVisitor"
);
export const setAddSubmission = createAction(
  "SET_ADD_SUBMISSION",
  "addSubmission"
);
export const setMapData = createAction("SET_MAP_DATA", "locationData");
export const updateMapData = createAction("UPDATE_MAP_DATA", "locationData");
export const removeMapData = createAction("REMOVE_MAP_DATA", "locationData");
export const resetMapData = createAction("RESET_MAP_DATA", "locationData");
export const setEditSubmission = createAction(
  "SET_EDIT_SUBMISSION",
  "editSubmission"
);
export const setSubmissions = createAction("SET_SUBMISSION", "submissions");
export const setDiscoverProgram = createAction("SET_DISCOVER_PROGRAM", "discover");
export const setAssetsId = createAction("SET_ASSETS_ID", "assetIdData");
export const setUpdate = createAction("SET_UPDATE", "update");
export const setUpdateDetails = createAction("SET_UPDATE_DETAILS", "updateDetails");
export const RECORD_MAP_INTERACTION = "RECORD_MAP_INTERACTION";
export const REMOVE_PENDING_MAP_INTERACTION = "REMOVE_PENDING_MAP_INTERACTION";
export const CLEAR_PENDING_MAP_INTERACTIONS = "CLEAR_PENDING_MAP_INTERACTIONS";

export const getCeToken = () => async (dispatch, getState) => {
  if (dispatch && typeof dispatch === "function") {
    try {
      await dispatch(sendPendingMapInteractions());
    } catch (err) {
      console.warn("Error", err?.message || err);
    }
  }
  await fetchCeToken()(dispatch, getState);
};

// Fetches and stores a new cePublic token, without first flushing queued map
// interactions as getCeToken does. The axios token interceptor refreshes
// through this one: a flush request carrying an expired token waits for that
// refresh, so a refresh that waited for the flush would never finish.
export const fetchCeToken = () => async (dispatch, getState) => {
  try {
    const { IDToken } = await fetchData(
      "getCeToken",
      "GET",
      null,
      dispatch,
      setCEToken,
      true,
      false,
      getState
    );
    localStorage.setItem("token", IDToken);
  } catch (error) {
    console.error("Error: ", error.message);
  }
};



let SendingPendingMapInteractions = false;

export const recordMapInteractionAction = (payload) => ({
  type: RECORD_MAP_INTERACTION,
  payload,
});

export const recordMapInteraction = ({ ID, key, sendNow = true }) => async (dispatch, getState) => {
  dispatch(recordMapInteractionAction({ ID, key }));
  try {
    const pending = getState()?.pendingMapInteractions || [];
    localStorage.setItem("pendingMapInteractions", JSON.stringify(pending));
  } catch (err) {
    console.log("error:", err);
  }

  if (sendNow) {
    try {
      await dispatch(sendPendingMapInteractions());
    } catch (err) {
      console.warn(err?.message || err);
    }
  }
};

export const sendPendingMapInteractions = () => async (dispatch, getState) => {
  if (SendingPendingMapInteractions) return;
  const pending = getState()?.pendingMapInteractions || [];
  if (!pending || pending.length === 0) return;

  SendingPendingMapInteractions = true;
  try {
    if (pending.length === 1) {
      const entry = pending[0];
      const payload = { action: "map-interactions", ...entry };
      await dispatch(createVisitor(payload));
    } else {
      const payload = { action: "map-interactions", interactions: pending };
      await dispatch(createVisitor(payload));
    }
    dispatch({ type: CLEAR_PENDING_MAP_INTERACTIONS });
    try {
      localStorage.removeItem("pendingMapInteractions");
    } catch (e) { }
  } catch (err) {
    console.warn(err?.message || err);
  } finally {
    SendingPendingMapInteractions = false;
  }
};


export const getCEAssetId = (data) => async (dispatch, getState) => {
  await fetchData("getceAssets", "POST", data, dispatch, setAssetsId, false, false, getState);
};

export const getAssets = (data) => async (dispatch, getState) => {
  await fetchData("getceAssets", "POST", data, dispatch, setAssets, false, false, getState);
};

export const getAssetComponents = (data) => async (dispatch, getState) => {
  await fetchData(
    "getAssetComponents",
    "POST",
    data,
    dispatch,
    setAssetComponents,
    false,
    false,
    getState
  );
};

export const getCeQuestion = (data) => async (dispatch, getState) => {
  await fetchData("getceQuestion", "POST", data, dispatch, setCeQuestion, false, false, getState);
};

// createVisitor is now only used for map-interactions
export const createVisitor = (data) => async (dispatch, getState) => {
  await fetchData("createVisitor", "POST", data, dispatch, setCreateVisitor, true, false, getState);
};

export const addSubmission = (data) => async (dispatch, getState) => {
  return fetchData("addSubmission", "POST", data, dispatch, setAddSubmission, false, false, getState);
};

export const editSubmission = (data) => async (dispatch, getState) => {
  return fetchData("editSubmission", "POST", data, dispatch, setEditSubmission, false, false, getState);
};

export const getSubmissions = (data) => async (dispatch, getState) => {
  return fetchData("getSubmissions", "POST", data, dispatch, setSubmissions, false, false, getState);
};

export const getDiscoverProgramData = (data) => async (dispatch, getState) => {
  await fetchData("discover", "POST", data, dispatch, setDiscoverProgram, false, false, getState);
};

// chatCreateVisitor sets chat terms acceptance state directly (no backend call)
export const chatCreateVisitor = ({ visitorID }) => (dispatch) => {
  dispatch(setChatCreateVisitor({ visitorID }));
};

export const addQAFeedback = (data) => async (dispatch, getState) => {
  await fetchData("addQAFeedback", "POST", data, dispatch, setAddQAFeedback, false, false, getState);
}

export const editQAFeedback = (data) => async (dispatch, getState) => {
  await fetchData("editQAFeedback", "POST", data, dispatch, setEditQAFeedback, false, false, getState);
}

export const getUpdate = (data) => async (dispatch, getState) => {
  await fetchData("getUpdate", "POST", data, dispatch, setUpdate, false, false, getState);
}

export const getUpdateDetails = (data) => async (dispatch, getState) => {
  await fetchData("getUpdate", "POST", data, dispatch, setUpdateDetails, false, false, getState);
}

/**
 * Awaitable page-view beacon (anonymised IP, legitimate interest).
 * Writes to ceVisitors via the Lambda@Edge analytics endpoint.
 * Fires on every page render (no execution gate).
 * visitorID is managed via sessionStorage inside analyticsBeacon.js.
 */
export const sendAnalyticsBeacon = ({ assetID, phase, pageName }) => async (dispatch) => {
  const result = await _sendPageView(assetID, phase, pageName);
  if (result?.visitorID) {
    dispatch(setCreateVisitor({ visitorID: result.visitorID }));
  }
  return result;
};

/**
 * Awaitable consent beacon (full IP, explicit consent).
 * Writes to ceConsents and returns requestId for consent linking.
 */
export const sendConsentBeacon = ({ assetID, policyUrls, phase, pageName }) => async () => {
  const result = await _sendConsent(assetID, policyUrls, phase, pageName);
  return result?.requestId || null;
};

/**
 * Send a message to the AI RAG chat endpoint.
 * Returns the response data directly for local state management.
 */
export const sendAiRagChatMessage = (data) => async (dispatch, getState) => {
  const { isGuest, useV2, ...payload } = data || {};
  const chatEndpoint = useV2 ? "aiRagChat2" : "aiRagChat";
  if (isGuest) {
    const guestJwt = getGuestJwt();
    if (!guestJwt) {
      throw new Error("guestSessionExpired");
    }
    const response = await axios.post(Config.dev.url[chatEndpoint], payload, {
      headers: { Authorization: `Bearer ${guestJwt}` },
    });
    dispatch(setAiRagChat(response.data));
    return response?.data;
  }

  const response = await fetchData(chatEndpoint, "POST", payload, dispatch, setAiRagChat, false, true, getState);
  return response;
};

/**
 * Chat2 consent acceptance - sets visitor state and fires consent beacon.
 */
export const chat2CreateVisitor = ({ visitorID }) => (dispatch) => {
  dispatch(setChat2CreateVisitor({ visitorID }));
};

/* ------------------------------------------------------------------ */
/* Guest chat auth                                                     */
/* ------------------------------------------------------------------ */

export const GUEST_REQUEST_CODE_PENDING = "GUEST_REQUEST_CODE_PENDING";
export const GUEST_REQUEST_CODE_SUCCESS = "GUEST_REQUEST_CODE_SUCCESS";
export const GUEST_REQUEST_CODE_ERROR = "GUEST_REQUEST_CODE_ERROR";
export const GUEST_VERIFY_CODE_PENDING = "GUEST_VERIFY_CODE_PENDING";
export const GUEST_VERIFY_CODE_SUCCESS = "GUEST_VERIFY_CODE_SUCCESS";
export const GUEST_VERIFY_CODE_ERROR = "GUEST_VERIFY_CODE_ERROR";
export const GUEST_SESSION_CLEARED = "GUEST_SESSION_CLEARED";

// Verify failures a guest cannot fix by retyping: the one-time code is gone,
// spent or attached to a session the server no longer holds. The login page
// reissues a code for these instead of sending the guest back to the keypad.
export const GUEST_STALE_CODE_KEYS = [
  "guestCodeExpiredReenterEmail",
  "guestSessionAlreadyVerified",
];

const mapGuestError = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code || error?.response?.data?.error;
  const message = error?.response?.data?.message || "";
  const meta = {
    expiresAt: error?.response?.data?.expiresAt,
  };
  if (status === 410 || code === "INVITATION_EXPIRED") {
    return { messageKey: "guestInvitationExpired", meta };
  }
  if (status === 429) return { messageKey: "guestRateLimited", meta };
  if (code === "INVITATION_RESOURCE_MISMATCH") {
    return { messageKey: "guestInvitationWrongPage", meta };
  }
  if (code === "EMAIL_SEND_FAILED") {
    return { messageKey: "guestEmailSendFailed", meta };
  }
  if (code === "SESSION_ALREADY_VERIFIED" || /already verified/i.test(message)) {
    return { messageKey: "guestSessionAlreadyVerified", meta };
  }
  if (
    code === "EXPIRED" ||
    code === "CODE_EXPIRED" ||
    code === "INVALID_SESSION" ||
    /code expired/i.test(message) ||
    /expired session/i.test(message)
  ) {
    return { messageKey: "guestCodeExpiredReenterEmail", meta };
  }
  if (status === 400 || status === 401) return { messageKey: "guestCodeInvalid", meta };
  return { messageKey: "guestCodeInvalid", meta };
};

// Which kind of invitation a sign-in page admits. The server refuses an
// invitation of the other kind, so a report guest can never be signed in to a
// chat (or a chat guest to a report) by following the wrong page. Absent means
// a chat, as it always has.
export const GUEST_RESOURCE_QUERY_REPORT = "queryReport";

export const guestRequestCode = ({ inviteToken, invitationToken, email, resourceType }) => async (dispatch) => {
  dispatch({ type: GUEST_REQUEST_CODE_PENDING });
  try {
    const response = await axios.post(Config.dev.url.guestRequestCode, {
      inviteToken: inviteToken || invitationToken,
      email,
      ...(resourceType ? { resourceType } : {}),
    });
    const sessionID = response?.data?.sessionID;
    dispatch({ type: GUEST_REQUEST_CODE_SUCCESS, payload: { sessionID } });
    return { sessionID };
  } catch (error) {
    const { messageKey, meta } = mapGuestError(error);
    dispatch({ type: GUEST_REQUEST_CODE_ERROR, payload: { messageKey, meta } });
    throw Object.assign(new Error(messageKey), { messageKey, meta });
  }
};

export const guestVerifyCode = ({
  sessionID,
  code,
  termsAcceptedAt,
  privacyAcceptedAt,
  resourceType,
}) => async (dispatch) => {
  dispatch({ type: GUEST_VERIFY_CODE_PENDING });
  try {
    const response = await axios.post(Config.dev.url.guestVerifyCode, {
      sessionID,
      code,
      termsAcceptedAt,
      privacyAcceptedAt,
      ...(resourceType ? { resourceType } : {}),
    });
    const guestJwt = response?.data?.guestJwt || response?.data?.token;
    if (!guestJwt) {
      throw new Error("guestCodeInvalid");
    }
    // Each kind of guest keeps its token in its own slot.
    if (resourceType === GUEST_RESOURCE_QUERY_REPORT) {
      reportGuestSession.setJwt(guestJwt);
    } else {
      setGuestJwt(guestJwt);
    }
    dispatch({ type: GUEST_VERIFY_CODE_SUCCESS, payload: { guestJwt } });
    return { guestJwt };
  } catch (error) {
    const { messageKey, meta } = mapGuestError(error);
    dispatch({ type: GUEST_VERIFY_CODE_ERROR, payload: { messageKey, meta } });
    throw Object.assign(new Error(messageKey), { messageKey, meta });
  }
};

// A large report comes back a page at a time - extractions and, for newer
// runs, recommendations each carry their own cursor. Bounded, and a cursor
// that fails to advance stops the loop, so a malformed response cannot keep a
// guest's browser fetching forever.
const MAX_GUEST_REPORT_PAGES = 100;

const uniqueByID = (items) =>
  [...new Map(items.map((item) => [item?.ID || JSON.stringify(item), item])).values()];

const mergeReportPage = (current, next) => {
  const merged = { ...current, ...next };
  if (Array.isArray(current.extractions) || Array.isArray(next.extractions)) {
    merged.extractions = uniqueByID([...(current.extractions || []), ...(next.extractions || [])]);
  }
  if (current.recommendations || next.recommendations) {
    const keys = new Set([
      ...Object.keys(current.recommendations || {}),
      ...Object.keys(next.recommendations || {}),
    ]);
    merged.recommendations = Object.fromEntries([...keys].map((key) => {
      const a = current.recommendations?.[key];
      const b = next.recommendations?.[key];
      return [key, Array.isArray(a) || Array.isArray(b) ? uniqueByID([...(a || []), ...(b || [])]) : (b ?? a)];
    }));
  }
  return merged;
};

const guestReportError = (messageKey) => Object.assign(new Error(messageKey), { messageKey });

/**
 * Fetch the one query report a guest was invited to, every page of it.
 *
 * The token names the report; the server re-checks the invitation and the
 * report's own sharing flag on every request - every page included - so
 * nothing here needs to be trusted beyond the queryID we ask for.
 */
export const getGuestQueryReport = (queryID) => async () => {
  const guestJwt = reportGuestSession.getJwt();
  if (!guestJwt) {
    throw guestReportError("guestSessionExpired");
  }
  const fetchPage = async (cursor) => {
    try {
      const response = await axios.get(Config.dev.url.aiQueries, {
        params: { queryID, includeExtractions: "true", ...cursor },
        headers: { Authorization: `Bearer ${guestJwt}` },
      });
      return response?.data?.query || null;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) throw guestReportError("guestSessionExpired");
      // Sharing switched off, or the invitation revoked or expired, since the
      // token was issued.
      if (status === 403) throw guestReportError("guestReportUnavailable");
      throw guestReportError("guestReportLoadFailed");
    }
  };

  let report = await fetchPage({});
  if (!report) return null;
  const seen = new Set();
  for (let page = 0; report.recommendationsNextToken || report.extractionsNextToken; page += 1) {
    const type = report.recommendationsNextToken ? "recommendations" : "extractions";
    const token = report[`${type}NextToken`];
    if (page >= MAX_GUEST_REPORT_PAGES || seen.has(`${type}:${token}`)) {
      // Show what arrived rather than nothing; the page says it is partial.
      return { ...report, partial: true };
    }
    seen.add(`${type}:${token}`);
    const next = await fetchPage({ [`${type}Cursor`]: token });
    if (!next || next.ID !== report.ID) {
      return { ...report, partial: true };
    }
    // Only this type's cursor moves on. A report can carry both at once, and
    // the other type's cursor must survive until its own turn comes.
    report = mergeReportPage(report, next);
    report[`${type}NextToken`] = next[`${type}NextToken`] || undefined;
  }
  return report;
};

export const clearGuestSession = () => (dispatch) => {
  clearGuestJwt();
  dispatch({ type: GUEST_SESSION_CLEARED });
};

// The report guest's counterpart. Leaves any chat session in the tab alone.
export const clearGuestReportSession = () => (dispatch) => {
  reportGuestSession.clearJwt();
  dispatch({ type: GUEST_SESSION_CLEARED });
};

/**
 * Configure a global axios interceptor that:
 *  - attaches the chat guest JWT as Authorization: Bearer ... when present
 *  - on 401 clears the chat guest session and redirects to the chat login page
 *
 * Chat guests only. A report guest's requests carry their own token and their
 * pages handle a lapsed session themselves (GuestReportGate sends them back to
 * the report sign-in), so a request already carrying the report token is left
 * exactly as it is - neither re-signed with a chat token nor sent to the chat.
 *
 * Safe to call multiple times; only registers once.
 */
const carriesReportGuestToken = (requestConfig) => {
  const reportToken = reportGuestSession.getJwt();
  const headers = requestConfig?.headers || {};
  const auth = typeof headers.get === "function"
    ? headers.get("Authorization")
    : headers.Authorization || headers.authorization;
  return !!reportToken && auth === `Bearer ${reportToken}`;
};

let guestInterceptorRegistered = false;
export const installGuestAuthInterceptor = () => {
  if (guestInterceptorRegistered) return;
  guestInterceptorRegistered = true;

  axios.interceptors.request.use((requestConfig) => {
    const token = getGuestJwt();
    if (!token || carriesReportGuestToken(requestConfig)) return requestConfig;
    const guestUrls = [
      Config.dev.url.aiRagChat,
      Config.dev.url.aiRagChat2,
      Config.dev.url.aiDocumentViewer,
      Config.dev.url.userQA,
    ].filter(Boolean);
    const isGuestApiCall = guestUrls.some(
      (u) => typeof requestConfig.url === "string" && requestConfig.url.startsWith(u)
    );
    if (isGuestApiCall) {
      requestConfig.headers = {
        ...(requestConfig.headers || {}),
        Authorization: `Bearer ${token}`,
      };
    }
    return requestConfig;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const hasGuestToken = !!getGuestJwt();
      if (status === 401 && hasGuestToken && !carriesReportGuestToken(error?.config)) {
        clearGuestJwt();
        const assetURLCode = window?.location?.pathname?.split("/")?.[1];
        if (assetURLCode) {
          window.location.assign(`/${assetURLCode}/chat2-guest`);
        }
      }
      return Promise.reject(error);
    }
  );
};
